import { Button, ButtonLevel } from '@vizit/atoms';
import { useDebounce } from '@vizit/hooks';
import { SearchSmallIcon, XCloseIcon } from '@vizit/icons';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectPopover,
  SelectTrigger,
  SelectValue,
  TextField,
  type TextFieldProps,
} from '@vizit/molecules';
import { cva, cx } from '@vizit/theme';
import React, { type Key } from 'react';
import { useSearchTextFieldState } from './search-textfield.state';

const searchInputClassNameVariants = cva({
  base: 'min-w-[280px] flex-1',
  variants: {
    hasSearchTypes: {
      true: '',
      false: '',
    },
    variant: {
      default: '',
      filled: '',
    },
  },
});

const FILLED_GROUP_CLASS_NAME =
  'border-0 bg-gray-utility-50 rounded-xxs focus-within:border-0 focus-within:px-6 focus-within:py-5';

const clearButtonClassNameVariants = cva({
  base: 'text-gray-utility-400 absolute right-2 top-1/2 -translate-y-1/2',
  variants: {
    hasValue: {
      true: 'visible',
      false: 'invisible',
    },
  },
});

interface SearchTextFieldProps extends Omit<TextFieldProps, 'startIcon' | 'placeholder'> {
  /**
   * The debounce delay in milliseconds
   * @optional
   * @type {number}
   * @default 500
   */
  debounceDelay?: number;
  /**
   * The default search type to select
   * @optional
   * @type {string}
   */
  defaultSearchType?: string;
  /**
   * The default value for the search field
   * @optional
   * @type {string}
   */
  defaultValue?: string;
  /**
   * The placeholder text for the search field
   * @optional
   * @type {string}
   * @default "Search"
   */
  placeholder?: string;
  /**
   * Array of search type options
   * @optional
   * @type {string[]}
   */
  searchTypes?: string[];
  /**
   * Callback fired when the search value changes (debounced)
   * @optional
   * @type {(value: string) => void}
   */
  onSearch?: (value: string) => void;
  /**
   * Callback fired when the search type changes
   * @optional
   * @type {(key: Key | null) => void}
   */
  onSearchByChange?: (key: Key | null) => void;
  /**
   * Visual variant of the search field
   * @optional
   * @type {'default' | 'filled'}
   * @default 'default'
   */
  variant?: 'default' | 'filled';
}

/**
 * A text field component specifically designed for search functionality.
 * Features a search icon, customizable placeholder, debounced search callback,
 * and optional dropdown to filter by search type.
 *
 * @param props - The component props
 *
 * @example
 * <SearchTextField
 *   debounceDelay={500}
 *   defaultSearchType="Name"
 *   defaultValue="Initial search term"
 *   placeholder="Search products..."
 *   searchTypes={['Name', 'SKU', 'Category']}
 *   onSearch={(value) => console.log('Search:', value)}
 *   onSearchByChange={(key) => console.log('Search type:', key)}
 * />
 */
function SearchTextField(props: SearchTextFieldProps) {
  const {
    className,
    debounceDelay = 500,
    defaultSearchType,
    defaultValue = '',
    placeholder = 'Search',
    searchTypes,
    onSearch,
    onSearchByChange,
    variant = 'default',
    ...other
  } = props;

  const { state, onSearchValueChange, onSearchTypeChange, onClear } = useSearchTextFieldState({
    defaultValue,
    defaultSearchType,
    onSearchByChange,
  });

  const debouncedSearchValue = useDebounce(state.searchValue, debounceDelay);
  const hasSearchTypes = React.useMemo(() => searchTypes !== undefined, [searchTypes]);

  React.useEffect(() => {
    if (onSearch) {
      onSearch(debouncedSearchValue);
    }
  }, [debouncedSearchValue, onSearch]);

  return (
    <div className={cx('flex items-end justify-end', className)}>
      <div className="relative flex-1">
        <TextField
          {...other}
          aria-label="Search"
          className={searchInputClassNameVariants({ hasSearchTypes, variant })}
          groupClassName={variant === 'filled' ? FILLED_GROUP_CLASS_NAME : undefined}
          inputProps={{ className: 'pr-24' }}
          placeholder={placeholder}
          startIcon={<SearchSmallIcon size={20} className="text-gray-utility-400" />}
          value={state.searchValue}
          onChange={onSearchValueChange}
        />
        <Button
          aria-label="Clear search"
          className={clearButtonClassNameVariants({ hasValue: !!state.searchValue })}
          level={ButtonLevel['Text-Secondary']}
          onPress={onClear}
        >
          <XCloseIcon size={16} />
        </Button>
      </div>
      {hasSearchTypes && (
        <Select
          aria-label="Search by"
          placeholder="Search by"
          selectedKey={state.searchType}
          onSelectionChange={onSearchTypeChange}
        >
          <SelectTrigger className="min-w-[120px] border-l-0">
            <SelectValue />
          </SelectTrigger>
          <SelectPopover>
            <SelectContent>
              {searchTypes?.map((type) => (
                <SelectItem
                  key={type.trim().toLowerCase().replace(' ', '-')}
                  id={type.trim().toLowerCase().replace(' ', '-')}
                  textValue={`By ${type}`}
                >
                  By {type}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectPopover>
        </Select>
      )}
    </div>
  );
}

export { SearchTextField };
export type { SearchTextFieldProps };
